import { useEffect, useRef, useState } from "react";
import { Renderer, Program, Triangle, Mesh } from "ogl";
import "./LightRays.css";

type LightRaysOrigin =
  | "top-left"
  | "top-center"
  | "top-right"
  | "left"
  | "right"
  | "bottom-left"
  | "bottom-center"
  | "bottom-right";

type UniformValue = [number, number] | [number, number, number] | number;

type LightRaysUniforms = Record<string, { value: UniformValue | number[] }>; // OGL uniforms signature

interface LightRaysProps {
  raysOrigin?: LightRaysOrigin;
  raysColor?: string;
  raysSpeed?: number;
  lightSpread?: number;
  rayLength?: number;
  pulsating?: boolean;
  fadeDistance?: number;
  saturation?: number;
  followMouse?: boolean;
  mouseInfluence?: number;
  noiseAmount?: number;
  distortion?: number;
  className?: string;
}

const DEFAULT_COLOR = "#ffffff";

const hexToRgb = (hex: string): [number, number, number] => {
  const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!match) {
    return [1, 1, 1];
  }

  return [
    parseInt(match[1], 16) / 255,
    parseInt(match[2], 16) / 255,
    parseInt(match[3], 16) / 255,
  ];
};

const OUTSIDE_PADDING = 0.2;

const getAnchorAndDir = (origin: LightRaysOrigin, w: number, h: number) => {
  switch (origin) {
    case "top-left":
      return { anchor: [0, -OUTSIDE_PADDING * h] as [number, number], dir: [0, 1] as [number, number] };
    case "top-right":
      return { anchor: [w, -OUTSIDE_PADDING * h] as [number, number], dir: [0, 1] as [number, number] };
    case "left":
      return { anchor: [-OUTSIDE_PADDING * w, 0.5 * h] as [number, number], dir: [1, 0] as [number, number] };
    case "right":
      return {
        anchor: [(1 + OUTSIDE_PADDING) * w, 0.5 * h] as [number, number],
        dir: [-1, 0] as [number, number],
      };
    case "bottom-left":
      return { anchor: [0, (1 + OUTSIDE_PADDING) * h] as [number, number], dir: [0, -1] as [number, number] };
    case "bottom-center":
      return {
        anchor: [0.5 * w, (1 + OUTSIDE_PADDING) * h] as [number, number],
        dir: [0, -1] as [number, number],
      };
    case "bottom-right":
      return {
        anchor: [w, (1 + OUTSIDE_PADDING) * h] as [number, number],
        dir: [0, -1] as [number, number],
      };
    case "top-center":
    default:
      return { anchor: [0.5 * w, -OUTSIDE_PADDING * h] as [number, number], dir: [0, 1] as [number, number] };
  }
};

const vertShader = `attribute vec2 position;\nvarying vec2 vUv;\nvoid main() {\n  vUv = position * 0.5 + 0.5;\n  gl_Position = vec4(position, 0.0, 1.0);\n}`;

const fragShader = `precision highp float;\nuniform float iTime;\nuniform vec2  iResolution;\nuniform vec2  rayPos;\nuniform vec2  rayDir;\nuniform vec3  raysColor;\nuniform float raysSpeed;\nuniform float lightSpread;\nuniform float rayLength;\nuniform float pulsating;\nuniform float fadeDistance;\nuniform float saturation;\nuniform vec2  mousePos;\nuniform float mouseInfluence;\nuniform float noiseAmount;\nuniform float distortion;\n\nvarying vec2 vUv;\n\nfloat noise(vec2 st) {\n  return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);\n}\n\nfloat rayStrength(vec2 raySource, vec2 rayRefDirection, vec2 coord,\n                  float seedA, float seedB, float speed) {\n  vec2 sourceToCoord = coord - raySource;\n  vec2 dirNorm = normalize(sourceToCoord);\n  float cosAngle = dot(dirNorm, rayRefDirection);\n\n  float distortedAngle = cosAngle + distortion * sin(iTime * 2.0 + length(sourceToCoord) * 0.01) * 0.2;\n  \n  float spreadFactor = pow(max(distortedAngle, 0.0), 1.0 / max(lightSpread, 0.001));\n\n  float distance = length(sourceToCoord);\n  float maxDistance = iResolution.x * rayLength;\n  float lengthFalloff = clamp((maxDistance - distance) / maxDistance, 0.0, 1.0);\n  \n  float fadeFalloff = clamp((iResolution.x * fadeDistance - distance) / (iResolution.x * fadeDistance), 0.5, 1.0);\n  float pulse = pulsating > 0.5 ? (0.8 + 0.2 * sin(iTime * speed * 3.0)) : 1.0;\n\n  float baseStrength = clamp(\n    (0.45 + 0.15 * sin(distortedAngle * seedA + iTime * speed)) +\n    (0.3 + 0.2 * cos(-distortedAngle * seedB + iTime * speed)),\n    0.0, 1.0\n  );\n\n  return baseStrength * lengthFalloff * fadeFalloff * spreadFactor * pulse;\n}\n\nvoid mainImage(out vec4 fragColor, in vec2 fragCoord) {\n  vec2 coord = vec2(fragCoord.x, iResolution.y - fragCoord.y);\n  \n  vec2 finalRayDir = rayDir;\n  if (mouseInfluence > 0.0) {\n    vec2 mouseScreenPos = mousePos * iResolution.xy;\n    vec2 mouseDirection = normalize(mouseScreenPos - rayPos);\n    finalRayDir = normalize(mix(rayDir, mouseDirection, mouseInfluence));\n  }\n\n  vec4 rays1 = vec4(1.0) *\n               rayStrength(rayPos, finalRayDir, coord, 36.2214, 21.11349,\n                           1.5 * raysSpeed);\n  vec4 rays2 = vec4(1.0) *\n               rayStrength(rayPos, finalRayDir, coord, 22.3991, 18.0234,\n                           1.1 * raysSpeed);\n\n  fragColor = rays1 * 0.5 + rays2 * 0.4;\n\n  if (noiseAmount > 0.0) {\n    float n = noise(coord * 0.01 + iTime * 0.1);\n    fragColor.rgb *= (1.0 - noiseAmount + noiseAmount * n);\n  }\n\n  float brightness = 1.0 - (coord.y / iResolution.y);\n  fragColor.x *= 0.1 + brightness * 0.8;\n  fragColor.y *= 0.3 + brightness * 0.6;\n  fragColor.z *= 0.5 + brightness * 0.5;\n\n  if (saturation != 1.0) {\n    float gray = dot(fragColor.rgb, vec3(0.299, 0.587, 0.114));\n    fragColor.rgb = mix(vec3(gray), fragColor.rgb, saturation);\n  }\n\n  fragColor.rgb *= raysColor;\n}\n\nvoid main() {\n  vec4 color;\n  mainImage(color, gl_FragCoord.xy);\n  gl_FragColor  = color;\n}`;

const resizeRenderer = (
  container: HTMLDivElement,
  renderer: Renderer,
  uniforms: LightRaysUniforms,
  origin: LightRaysOrigin
) => {
  const { clientWidth: widthCSS, clientHeight: heightCSS } = container;
  const devicePixelRatio = Math.min(window.devicePixelRatio, 2);
  renderer.dpr = devicePixelRatio;
  renderer.setSize(widthCSS, heightCSS);

  const width = widthCSS * devicePixelRatio;
  const height = heightCSS * devicePixelRatio;

  uniforms.iResolution.value = [width, height];

  const { anchor, dir } = getAnchorAndDir(origin, width, height);
  uniforms.rayPos.value = anchor;
  uniforms.rayDir.value = dir;
};

const LightRays = ({
  raysOrigin = "top-center",
  raysColor = DEFAULT_COLOR,
  raysSpeed = 1,
  lightSpread = 1,
  rayLength = 2,
  pulsating = false,
  fadeDistance = 1,
  saturation = 1,
  followMouse = true,
  mouseInfluence = 0.1,
  noiseAmount = 0,
  distortion = 0,
  className = "",
}: LightRaysProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const uniformsRef = useRef<LightRaysUniforms | null>(null);
  const rendererRef = useRef<Renderer | null>(null);
  const meshRef = useRef<Mesh | null>(null);
  const animationIdRef = useRef<number | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const mouseRef = useRef({ x: 0.5, y: 0.5 });
  const smoothMouseRef = useRef({ x: 0.5, y: 0.5 });
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      setIsVisible(entry?.isIntersecting ?? false);
    }, { threshold: 0.1 });

    observer.observe(container);
    observerRef.current = observer;

    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!isVisible) {
      return;
    }

    const container = containerRef.current;
    if (!container) {
      return;
    }

    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }

    let disposed = false;

    const setup = async () => {
      await new Promise((resolve) => setTimeout(resolve, 10));
      if (disposed || !containerRef.current) {
        return;
      }

      const renderer = new Renderer({
        dpr: Math.min(window.devicePixelRatio, 2),
        alpha: true,
      });
      rendererRef.current = renderer;
      const gl = renderer.gl;
      const canvas = gl.canvas as HTMLCanvasElement;
      canvas.style.width = "100%";
      canvas.style.height = "100%";

      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
      container.appendChild(canvas);

      const geometry = new Triangle(gl);
      const uniforms: LightRaysUniforms = {
        iTime: { value: 0 },
        iResolution: { value: [1, 1] },
        rayPos: { value: [0, 0] },
        rayDir: { value: [0, 1] },
        raysColor: { value: hexToRgb(raysColor) },
        raysSpeed: { value: raysSpeed },
        lightSpread: { value: lightSpread },
        rayLength: { value: rayLength },
        pulsating: { value: pulsating ? 1 : 0 },
        fadeDistance: { value: fadeDistance },
        saturation: { value: saturation },
        mousePos: { value: [0.5, 0.5] },
        mouseInfluence: { value: mouseInfluence },
        noiseAmount: { value: noiseAmount },
        distortion: { value: distortion },
      };
      uniformsRef.current = uniforms;

      const program = new Program(gl, {
        vertex: vertShader,
        fragment: fragShader,
        uniforms,
      });
      const mesh = new Mesh(gl, { geometry, program });
      meshRef.current = mesh;

      const updatePlacement = () => {
        const currentContainer = containerRef.current;
        const currentRenderer = rendererRef.current;
        const currentUniforms = uniformsRef.current;
        if (!currentContainer || !currentRenderer || !currentUniforms) {
          return;
        }
        resizeRenderer(currentContainer, currentRenderer, currentUniforms, raysOrigin);
      };

      const renderLoop = (time: number) => {
        const currentRenderer = rendererRef.current;
        const currentUniforms = uniformsRef.current;
        const currentMesh = meshRef.current;
        if (!currentRenderer || !currentUniforms || !currentMesh) {
          return;
        }

        currentUniforms.iTime.value = time * 0.001;

        if (followMouse && mouseInfluence > 0) {
          const smoothing = 0.92;
          smoothMouseRef.current.x =
            smoothMouseRef.current.x * smoothing + mouseRef.current.x * (1 - smoothing);
          smoothMouseRef.current.y =
            smoothMouseRef.current.y * smoothing + mouseRef.current.y * (1 - smoothing);
          currentUniforms.mousePos.value = [
            smoothMouseRef.current.x,
            smoothMouseRef.current.y,
          ];
        }

        try {
          currentRenderer.render({ scene: currentMesh });
          animationIdRef.current = requestAnimationFrame(renderLoop);
        } catch (error) {
          console.warn("LightRays WebGL rendering error", error);
        }
      };

      window.addEventListener("resize", updatePlacement);
      updatePlacement();
      animationIdRef.current = requestAnimationFrame(renderLoop);

      cleanupRef.current = () => {
        if (animationIdRef.current) {
          cancelAnimationFrame(animationIdRef.current);
          animationIdRef.current = null;
        }

        window.removeEventListener("resize", updatePlacement);

        const currentRenderer = rendererRef.current;
        if (currentRenderer) {
          try {
            const loseContext = currentRenderer.gl.getExtension("WEBGL_lose_context");
            if (loseContext) {
              loseContext.loseContext();
            }
          } catch (error) {
            console.warn("Error releasing WebGL context", error);
          }

          const currentCanvas = currentRenderer.gl.canvas as HTMLCanvasElement;
          if (currentCanvas.parentNode) {
            currentCanvas.parentNode.removeChild(currentCanvas);
          }
        }

        rendererRef.current = null;
        meshRef.current = null;
        uniformsRef.current = null;
      };
    };

    setup();

    return () => {
      disposed = true;
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, [
    isVisible,
    raysOrigin,
    raysColor,
    raysSpeed,
    lightSpread,
    rayLength,
    pulsating,
    fadeDistance,
    saturation,
    followMouse,
    mouseInfluence,
    noiseAmount,
    distortion,
  ]);

  useEffect(() => {
    const uniforms = uniformsRef.current;
    const container = containerRef.current;
    const renderer = rendererRef.current;
    if (!uniforms || !container || !renderer) {
      return;
    }

    uniforms.raysColor.value = hexToRgb(raysColor);
    uniforms.raysSpeed.value = raysSpeed;
    uniforms.lightSpread.value = lightSpread;
    uniforms.rayLength.value = rayLength;
    uniforms.pulsating.value = pulsating ? 1 : 0;
    uniforms.fadeDistance.value = fadeDistance;
    uniforms.saturation.value = saturation;
    uniforms.mouseInfluence.value = mouseInfluence;
    uniforms.noiseAmount.value = noiseAmount;
    uniforms.distortion.value = distortion;

    const { dpr } = renderer;
    const { clientWidth, clientHeight } = container;
    const width = clientWidth * dpr;
    const height = clientHeight * dpr;
    const { anchor, dir } = getAnchorAndDir(raysOrigin, width, height);
    uniforms.rayPos.value = anchor;
    uniforms.rayDir.value = dir;
  }, [
    raysOrigin,
    raysColor,
    raysSpeed,
    lightSpread,
    rayLength,
    pulsating,
    fadeDistance,
    saturation,
    mouseInfluence,
    noiseAmount,
    distortion,
  ]);

  useEffect(() => {
    if (!followMouse) {
      return;
    }

    const handleMouseMove = (event: MouseEvent) => {
      const container = containerRef.current;
      if (!container) {
        return;
      }
      const rect = container.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width;
      const y = (event.clientY - rect.top) / rect.height;
      mouseRef.current = { x: Math.max(0, Math.min(1, x)), y: Math.max(0, Math.min(1, y)) };
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, [followMouse]);

  return (
    <div
      ref={containerRef}
      className={["light-rays-container", className].filter(Boolean).join(" ")}
    />
  );
};

export default LightRays;
